import { useState, useEffect, useMemo, useCallback, lazy, Suspense, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { usePushNotifications } from '@/hooks/usePushNotifications';
import { supabase, CUSTOM_SUPABASE_URL } from '@/lib/supabase';
import { deobfuscateMessage, isObfuscatedMessage } from '@/lib/simpleEncryption';
import {
  LayoutDashboard,
  MessageCircle,
  Search,
  Contact,
  ClipboardList,
  LifeBuoy,
  Shield,
  Settings,
  LogOut,
  Sun,
  Moon,
  Menu,
  MessageSquare,
  Grid,
  Wallet,
  Users,
  Snowflake,
  Bell,
} from 'lucide-react';
import SnowEffect from '@/components/effects/SnowEffect';
import DashboardView from '@/components/dashboard/DashboardView';
import ProfitModal from '@/components/modals/ProfitModal';
import SettingsModal from '@/components/settings/SettingsModal';
import logo from '@/assets/logo.png';
import TourSearchButton from '@/components/tours/TourSearchButton';
import SubscriptionIndicator from '@/components/subscription/SubscriptionIndicator';
import { toast } from 'sonner';

// Lazy loading для тяжелых компонентов
const AdminPanelView = lazy(() => import('@/components/admin/AdminPanelView'));
const ContactsView = lazy(() => import('@/components/contacts/ContactsView'));
const TasksView = lazy(() => import('@/components/tasks/TasksView'));
const TicketsView = lazy(() => import('@/components/tickets/TicketsView'));
const ToursView = lazy(() => import('@/components/tours/ToursView'));
const MessengerView = lazy(() => import('@/components/messenger/MessengerView'));
const ChatView = lazy(() => import('@/components/chat/ChatView'));
const ManagerPanelView = lazy(() => import('@/components/manager/ManagerPanelView'));

type Tab = 'dashboard' | 'chat' | 'messenger' | 'tours' | 'contacts' | 'tasks' | 'tickets' | 'admin' | 'manager';

export default function Dashboard() {
  const location = useLocation();
  const { user, profile, role, session, signOut } = useAuth();
  const { permissions, loading: permissionsLoading } = usePermissions();
  const { 
    requestPermission, 
    notifyNewMessage, 
    notifyNewTask, 
    notifyTaskUpdate, 
    notifySupportResponse, 
    isSupported: notificationsSupported,
    permission: notificationPermission 
  } = usePushNotifications();
  const [activeTab, setActiveTabState] = useState<Tab>('dashboard');
  
  // Wrapper to save tab to localStorage
  const setActiveTab = useCallback((tab: Tab) => {
    localStorage.setItem('activeTab', tab);
    setActiveTabState(tab);
  }, []);
  const [isDark, setIsDark] = useState(false);
  const [snowEnabled, setSnowEnabled] = useState(() => {
    return localStorage.getItem('snowEnabled') === 'true';
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem('notificationsEnabled') === 'true';
  });
  const notificationsEnabledRef = useRef(notificationsEnabled);
  
  // Keep ref in sync with state
  useEffect(() => {
    notificationsEnabledRef.current = notificationsEnabled;
  }, [notificationsEnabled]);
  
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showProfitModal, setShowProfitModal] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadTasks, setUnreadTasks] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [allTransactions, setAllTransactions] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);

  const debugAuth = useMemo(() => new URLSearchParams(location.search).get('debug') === '1', [location.search]);

  const jwtDebug = useMemo(() => {
    const token = session?.access_token;
    if (!token) return { ok: false, parts: 0, claims: null as Record<string, unknown> | null, error: 'no_token' };

    const parts = token.split('.');
    if (parts.length !== 3) {
      return { ok: false, parts: parts.length, claims: null as Record<string, unknown> | null, error: 'not_jwt' };
    }

    const payload = parts[1];
    if (!payload) return { ok: false, parts: parts.length, claims: null as Record<string, unknown> | null, error: 'no_payload' };

    try {
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
      const decoded = atob(padded);
      const claims = JSON.parse(decoded) as Record<string, unknown>;
      return { ok: true, parts: parts.length, claims, error: null as string | null };
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'decode_error';
      return { ok: false, parts: parts.length, claims: null as Record<string, unknown> | null, error: msg };
    }
  }, [session?.access_token]);

  // Global presence tracking - update online status every 30 seconds
  useEffect(() => {
    if (!user?.id) return;
    
    const updatePresence = async () => {
      try {
        // First check if record exists
        const { data: existing } = await supabase
          .from('user_presence')
          .select('id')
          .eq('user_id', user.id)
          .single();
        
        if (existing) {
          // Update existing record
          const { error } = await supabase
            .from('user_presence')
            .update({
              last_seen: new Date().toISOString(),
              is_online: true
            })
            .eq('user_id', user.id);
          
          if (error) console.error('Error updating presence:', error);
        } else {
          // Insert new record
          const { error } = await supabase
            .from('user_presence')
            .insert({
              user_id: user.id,
              last_seen: new Date().toISOString(),
              is_online: true
            });
          
          if (error) console.error('Error inserting presence:', error);
        }
      } catch (err) {
        console.error('Presence update failed:', err);
      }
    };
    
    // Initial update
    updatePresence();
    
    // Update every 30 seconds
    const presenceInterval = setInterval(updatePresence, 30000);
    
    // Set offline on unmount
    const setOffline = async () => {
      try {
        await supabase
          .from('user_presence')
          .update({
            last_seen: new Date().toISOString(),
            is_online: false
          })
          .eq('user_id', user.id);
      } catch (err) {
        console.error('Error setting offline:', err);
      }
    };
    
    return () => {
      clearInterval(presenceInterval);
      setOffline();
    };
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      // Оптимизация: загружаем данные параллельно
      Promise.all([
        loadTransactions(),
        loadUsers(),
        loadUnreadTasks(),
        loadUnreadTickets(),
        loadUnreadMessages()
      ]);
      
      // Подписка на изменения транзакций
      const transactionsChannel = supabase
        .channel('transactions-changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'transactions',
          filter: `user_id=eq.${user.id}`
        }, (payload) => {
          if (payload.eventType === 'INSERT') {
            setAllTransactions(prev => {
              const exists = prev.some(t => t.id === payload.new.id);
              if (exists) return prev;
              return [payload.new, ...prev];
            });
          } else if (payload.eventType === 'UPDATE') {
            setAllTransactions(prev => prev.map(t => 
              t.id === payload.new.id ? payload.new : t
            ));
          } else if (payload.eventType === 'DELETE') {
            setAllTransactions(prev => prev.filter(t => t.id !== payload.old.id));
          }
        })
        .subscribe();
      
      // Подписка на изменения tasks
      const tasksChannel = supabase
        .channel('tasks-changes')
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tasks' }, (payload) => {
          loadUnreadTasks();
          // Push notification for task updates - use ref for latest value
          if (notificationsEnabledRef.current && payload.new) {
            const task = payload.new as any;
            if (task.user_id === user.id || task.creator_id === user.id) {
              console.log('Sending task update notification:', task.title);
              notifyTaskUpdate(task.title, task.status);
            }
          }
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tasks' }, (payload) => {
          loadUnreadTasks();
          // Push notification for new tasks - use ref for latest value
          if (notificationsEnabledRef.current && payload.new) {
            const task = payload.new as any;
            if (task.user_id === user.id && task.creator_id !== user.id) {
              console.log('Sending new task notification:', task.title);
              notifyNewTask(task.title);
            }
          }
        })
        .subscribe();
        
      const ticketsChannel = supabase
        .channel('tickets-changes')
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'tickets' }, (payload) => {
          loadUnreadTickets();
          // Push notification for ticket updates - use ref for latest value
          if (notificationsEnabledRef.current && payload.new) {
            const ticket = payload.new as any;
            if (ticket.creator_id === user.id && !ticket.is_read_by_creator) {
              console.log('Sending support response notification:', ticket.title);
              notifySupportResponse(ticket.title);
            }
          }
        })
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tickets' }, () => {
          loadUnreadTickets();
        })
        .subscribe();
        
      const ticketCommentsChannel = supabase
        .channel('ticket-comments-changes')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'ticket_comments' }, async (payload) => {
          loadUnreadTickets();
          // Push notification for new ticket comments - use ref for latest value
          if (notificationsEnabledRef.current && payload.new) {
            const comment = payload.new as any;
            // Get ticket info
            const { data: ticket } = await supabase
              .from('tickets')
              .select('title, creator_id')
              .eq('id', comment.ticket_id)
              .single();
            if (ticket && ticket.creator_id === user.id) {
              console.log('Sending ticket comment notification:', ticket.title);
              notifySupportResponse(ticket.title);
            }
          }
        })
        .subscribe();
        
      const messagesChannel = supabase
        .channel('chat-messages-changes')
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, async (payload) => {
          console.log('=== NEW MESSAGE EVENT ===', payload);
          const message = payload.new as any;
          if (message.sender_id !== user.id) {
            loadUnreadMessages();
            // Push notification for new messages - use ref for latest value
            console.log('Notifications enabled (ref):', notificationsEnabledRef.current);
            console.log('Notification permission:', Notification.permission);
            if (notificationsEnabledRef.current) {
              const { data: senderProfile } = await supabase
                .from('profiles')
                .select('name')
                .eq('id', message.sender_id)
                .single();
              
              // Get message preview - handle encrypted messages
              let messagePreview = message.text;
              if (message.type === 'file') {
                messagePreview = `📎 ${message.file_name || 'Файл'}`;
              } else if (messagePreview && isObfuscatedMessage(messagePreview)) {
                // Deobfuscate simple encrypted message for preview using correct function
                try {
                  messagePreview = deobfuscateMessage(messagePreview);
                } catch {
                  messagePreview = 'Новое сообщение';
                }
              } else if (messagePreview && messagePreview.length > 100) {
                // E2E encrypted - can't decrypt here
                messagePreview = '🔒 Зашифрованное сообщение';
              }
              
              // Truncate long messages
              if (messagePreview && messagePreview.length > 50) {
                messagePreview = messagePreview.substring(0, 50) + '...';
              }
              
              console.log('Sending notification for message from:', senderProfile?.name, 'preview:', messagePreview);
              const result = notifyNewMessage(senderProfile?.name || 'Пользователь', messagePreview);
              console.log('Notification result:', result);
            }
          }
        })
        .subscribe((status) => {
          console.log('Messages channel status:', status);
        });
        
      return () => {
        supabase.removeChannel(transactionsChannel);
        supabase.removeChannel(tasksChannel);
        supabase.removeChannel(ticketsChannel);
        supabase.removeChannel(ticketCommentsChannel);
        supabase.removeChannel(messagesChannel);
      };
    }
  }, [user, role, notifyNewMessage, notifyNewTask, notifyTaskUpdate, notifySupportResponse]);

  const loadUnreadMessages = useCallback(async () => {
    // Используем таблицу chat_unread_counts для точного подсчета
    const { data: unreadCounts } = await supabase
      .from('chat_unread_counts')
      .select('count')
      .eq('user_id', user?.id)
      .gt('count', 0);
    
    const totalUnread = unreadCounts?.reduce((sum, item) => sum + (item.count || 0), 0) || 0;
    setUnreadMessages(totalUnread);
  }, [user?.id]);
  
  const loadUnreadTasks = useCallback(async () => {
    // Непрочитанные задачи: назначенные мне или созданные мной с обновлениями
    const { data: myTasks } = await supabase
      .from('tasks')
      .select('*')
      .eq('user_id', user?.id)
      .eq('is_read', false);
      
    const { data: createdTasks } = await supabase
      .from('tasks')
      .select('*')
      .eq('creator_id', user?.id)
      .eq('is_read_by_creator', false)
      .neq('user_id', user?.id);
    
    setUnreadTasks((myTasks?.length || 0) + (createdTasks?.length || 0));
  }, [user?.id]);
  
  const loadUnreadTickets = useCallback(async () => {
    if (role === 'admin' || role === 'moderator') {
      // Для админов - непрочитанные тикеты от пользователей
      const { count } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .eq('is_read_by_admin', false);
      setUnreadTickets(count || 0);
    } else {
      // Для пользователей - их тикеты с обновлениями от админа
      const { count } = await supabase
        .from('tickets')
        .select('*', { count: 'exact', head: true })
        .eq('creator_id', user?.id)
        .eq('is_read_by_creator', false);
      setUnreadTickets(count || 0);
    }
  }, [user?.id, role]);
  
  const loadTransactions = useCallback(async () => {
    const { data } = await supabase
      .from('transactions')
      .select('*')
      .eq('user_id', user?.id)
      .order('date', { ascending: false });
    if (data) setAllTransactions(data);
  }, [user?.id]);
  
  const loadUsers = useCallback(async () => {
    const { data } = await supabase.from('profiles').select('*');
    if (data) setAllUsers(data);
  }, []);
  
  const handleAddTransaction = useCallback(async (transaction: any) => {
    const { data, error } = await supabase
      .from('transactions')
      .insert({
        user_id: user?.id,
        amount: transaction.amount,
        note: `${transaction.client}${transaction.destination ? ' - ' + transaction.destination : ''}`,
        date: transaction.date
      })
      .select()
      .single();
    
    if (error) {
      toast.error('Ошибка добавления транзакции');
      return;
    }
    
    if (data) {
      setAllTransactions(prev => [data, ...prev]);
      toast.success('Транзакция добавлена');
    }
  }, [user?.id]);
  
  const handleDeleteTransaction = useCallback(async (id: string) => {
    await supabase.from('transactions').delete().eq('id', id);
    setAllTransactions(prev => prev.filter(t => t.id !== id));
    toast.success('Транзакция удалена');
  }, []);

  const tabs = [
    { id: 'dashboard', label: 'Аналитика', icon: LayoutDashboard, permission: 'analytics' },
    { id: 'chat', label: 'Чат', icon: MessageSquare, permission: 'chat' },
    { id: 'messenger', label: 'Мессенджеры', icon: MessageCircle, permission: 'messengers' },
    { id: 'tours', label: 'Подбор тура', icon: Search, permission: 'tours' },
    { id: 'contacts', label: 'Контакты', icon: Contact, permission: 'contacts' },
    { id: 'tasks', label: 'Задачи', icon: ClipboardList, permission: 'tasks' },
    { id: 'tickets', label: 'Поддержка', icon: LifeBuoy, permission: 'support' },
    ...(role === 'manager' ? [{ id: 'manager', label: 'Панель руководителя', icon: Users, permission: 'analytics' }] : []),
    { id: 'admin', label: 'Панель управления', icon: Shield, permission: 'admin' },
  ];

  const filteredTabs = tabs.filter(tab => {
    const permissionKey = tab.permission as keyof typeof permissions;
    return permissions[permissionKey] === true;
  });

  // При загрузке проверяем, есть ли доступ к сохраненной вкладке
  useEffect(() => {
    if (permissionsLoading) return;
    
    const savedTab = localStorage.getItem('activeTab') as Tab;
    
    if (savedTab) {
      // Проверяем, есть ли сохраненная вкладка в списке доступных
      const hasAccess = filteredTabs.some(tab => tab.id === savedTab);
      
      if (hasAccess) {
        setActiveTabState(savedTab);
      } else {
        // Если нет доступа к сохраненной вкладке - открываем первую доступную
        const firstAvailable = filteredTabs[0];
        if (firstAvailable) {
          setActiveTabState(firstAvailable.id as Tab);
          localStorage.setItem('activeTab', firstAvailable.id);
        }
      }
    } else {
      // Если нет сохраненной вкладки - открываем первую доступную
      const firstAvailable = filteredTabs[0];
      if (firstAvailable) {
        setActiveTabState(firstAvailable.id as Tab);
      }
    }
  }, [permissionsLoading, filteredTabs.length, role]);

  const toggleTheme = useCallback(() => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle('dark');
  }, [isDark]);

  const toggleSnow = useCallback(() => {
    setSnowEnabled(prev => {
      const newValue = !prev;
      localStorage.setItem('snowEnabled', String(newValue));
      return newValue;
    });
  }, []);

  const toggleNotifications = useCallback(async () => {
    if (!notificationsSupported) {
      toast.error('Ваш браузер не поддерживает уведомления');
      return;
    }
    
    if (!notificationsEnabled) {
      // Check current permission state first
      const currentPermission = 'Notification' in window ? Notification.permission : 'denied';
      if (currentPermission === 'denied') {
        toast.error('Уведомления заблокированы в настройках браузера. Разрешите уведомления в настройках сайта.');
        return;
      }
      
      const granted = await requestPermission();
      if (granted) {
        setNotificationsEnabled(true);
        localStorage.setItem('notificationsEnabled', 'true');
        toast.success('Уведомления включены');
      } else {
        const updatedPermission = 'Notification' in window ? Notification.permission : 'denied';
        if (updatedPermission === 'denied') {
          toast.error('Уведомления заблокированы. Разрешите их в настройках браузера.');
        } else {
          toast.error('Не удалось получить разрешение на уведомления');
        }
      }
    } else {
      setNotificationsEnabled(false);
      localStorage.setItem('notificationsEnabled', 'false');
      toast.success('Уведомления отключены');
    }
  }, [notificationsEnabled, notificationsSupported, requestPermission]);

  const SidebarItem = useCallback(({ icon: Icon, label, active, onClick, isDanger = false, badge = 0 }: any) => (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl transition-all duration-300 relative touch-manipulation ${
        active
          ? isDanger
            ? 'bg-red-500 text-white shadow-lg shadow-red-200'
            : badge > 0
            ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
            : 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
          : badge > 0
          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 dark:active:bg-slate-700'
      }`}
    >
      <Icon size={18} className="md:w-5 md:h-5 flex-shrink-0" />
      <span className="font-medium text-xs md:text-sm flex-1 text-left truncate">{label}</span>
      {badge > 0 && (
        <span className="bg-white text-orange-600 dark:bg-slate-800 dark:text-orange-400 text-xs font-bold px-1.5 md:px-2 py-0.5 rounded-full flex-shrink-0">
          {badge}
        </span>
      )}
    </button>
  ), []);

  const ThemeToggle = useCallback(() => (
    <div className="space-y-2">
      <button
        onClick={toggleTheme}
        className={`relative w-full p-1 rounded-xl flex items-center justify-between border transition-all duration-300 ${
          isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-100 border-slate-200'
        }`}
      >
        <div
          className={`absolute top-1 left-1 w-[calc(50%-4px)] h-[calc(100%-8px)] bg-white dark:bg-slate-600 rounded-lg shadow-sm transition-all duration-300 ${
            isDark ? 'translate-x-full' : 'translate-x-0'
          }`}
        ></div>
        <div
          className={`flex-1 flex items-center justify-center gap-2 py-2 z-10 text-xs font-bold ${
            !isDark ? 'text-slate-800' : 'text-slate-500'
          }`}
        >
          <Sun size={14} /> Светлая
        </div>
        <div
          className={`flex-1 flex items-center justify-center gap-2 py-2 z-10 text-xs font-bold ${
            isDark ? 'text-white' : 'text-slate-400'
          }`}
        >
          <Moon size={14} /> Темная
        </div>
      </button>
      <label className={`flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-lg transition-colors ${
        snowEnabled ? 'bg-blue-100 dark:bg-blue-900/30' : ''
      }`}>
        <input
          type="checkbox"
          checked={snowEnabled}
          onChange={toggleSnow}
          className="w-4 h-4 rounded accent-blue-500"
        />
        <Snowflake size={14} className={snowEnabled ? 'text-blue-500' : 'text-slate-400'} />
        <span className={`text-xs font-medium ${snowEnabled ? 'text-blue-600 dark:text-blue-400' : 'text-slate-500'}`}>
          Включить снег
        </span>
      </label>
      {notificationsSupported && (
        <label className={`flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-lg transition-colors ${
          notificationsEnabled ? 'bg-green-100 dark:bg-green-900/30' : ''
        }`}>
          <input
            type="checkbox"
            checked={notificationsEnabled}
            onChange={toggleNotifications}
            className="w-4 h-4 rounded accent-green-500"
          />
          <Bell size={14} className={notificationsEnabled ? 'text-green-500' : 'text-slate-400'} />
          <span className={`text-xs font-medium ${notificationsEnabled ? 'text-green-600 dark:text-green-400' : 'text-slate-500'}`}>
            Уведомления
          </span>
        </label>
      )}
    </div>
  ), [isDark, toggleTheme, snowEnabled, toggleSnow, notificationsEnabled, toggleNotifications, notificationsSupported]);

  return (
    <div className={`flex h-screen overflow-hidden transition-colors duration-300 ${isDark ? 'bg-slate-900 text-white' : 'bg-[#F2F4F8] text-slate-800'}`}>
      {snowEnabled && <SnowEffect />}
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex w-64 border-r flex-col justify-between py-4 md:py-6 z-20 backdrop-blur-xl transition-colors ${
          isDark ? 'bg-slate-900/90 border-slate-800' : 'bg-white/80 border-slate-200'
        }`}
      >
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-2 md:gap-3 mb-6 md:mb-8">
            <img src={logo} alt="Travel Ease" className="h-20 md:h-24 w-auto" />
          </div>
          <nav className="space-y-2">
            {filteredTabs.map((tab) => {
              let badge = 0;
              if (tab.id === 'tasks') badge = unreadTasks;
              if (tab.id === 'tickets') badge = unreadTickets;
              if (tab.id === 'chat') badge = unreadMessages;
              
              return (
                <SidebarItem
                  key={tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  active={activeTab === tab.id}
                  onClick={() => setActiveTab(tab.id as Tab)}
                  badge={badge}
                />
              );
            })}
          </nav>
        </div>
        <div className={`px-6 border-t pt-4 ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
          <div className="mb-4">
            <ThemeToggle />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <img
              src={profile?.avatar || '/placeholder.svg?height=150&width=150'}
              className={`w-10 h-10 rounded-full border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
              alt="Avatar"
            />
            <div className="overflow-hidden">
              <div className={`text-sm font-bold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                {profile?.name}
              </div>
              <div className="text-xs text-slate-500 truncate capitalize">
                {role === 'manager'
                  ? 'Руководитель'
                  : role === 'admin'
                  ? 'Админ'
                  : role === 'moderator'
                  ? 'Модератор'
                  : role === 'user'
                  ? 'Пользователь'
                  : 'Сотрудник'}
              </div>
            </div>
          </div>
          <button 
            onClick={() => setShowSettings(true)}
            className="w-full flex items-center gap-2 text-slate-500 hover:text-blue-500 text-sm font-medium transition-colors mb-2"
          >
            <Settings size={18} /> <span>Настройки</span>
          </button>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 text-slate-500 hover:text-red-500 text-sm font-medium transition-colors"
          >
            <LogOut size={18} /> <span>Выйти</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col overflow-hidden pb-[70px] md:pb-0">
        {/* Header */}
        <header
          className={`h-16 border-b flex items-center justify-between px-4 md:px-8 z-10 backdrop-blur-sm ${
            isDark ? 'bg-slate-900/50 border-slate-800' : 'bg-white/50 border-slate-200'
          }`}
        >
          <h2 className={`text-lg md:text-xl font-semibold truncate mr-2 ${isDark ? 'text-slate-100' : 'text-slate-700'}`}>
            {tabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
          </h2>
          <div className="flex items-center gap-2 md:gap-4">
            <SubscriptionIndicator />
            {activeTab === 'dashboard' && (
              <button
                onClick={() => setShowProfitModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm font-bold"
              >
                <Wallet size={18} />
                <span className="hidden md:inline">Доход</span>
              </button>
            )}
          </div>
        </header>

        {debugAuth && (
          <section
            className={`px-4 md:px-8 py-3 border-b ${
              isDark ? 'border-slate-800 bg-slate-900/30' : 'border-slate-200 bg-white/30'
            }`}
          >
            <div className="text-xs font-mono space-y-1 break-all">
              <div>
                <span className="text-slate-500">Backend:</span> {CUSTOM_SUPABASE_URL}
              </div>
              <div>
                <span className="text-slate-500">Auth UID:</span> {user?.id ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">Email:</span> {user?.email ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">Role:</span> {role ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">Profile.name:</span> {profile?.name ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">JWT:</span>{' '}
                {jwtDebug.ok ? 'decoded' : `error: ${jwtDebug.error ?? '-'}`} (parts: {jwtDebug.parts})
              </div>
              <div>
                <span className="text-slate-500">JWT iss:</span> {(jwtDebug.claims as any)?.iss ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">JWT aud:</span> {(jwtDebug.claims as any)?.aud ?? '-'}
              </div>
              <div>
                <span className="text-slate-500">JWT role:</span> {(jwtDebug.claims as any)?.role ?? '-'}
              </div>
            </div>
          </section>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 custom-scroll">
          <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>}>
            {activeTab === 'dashboard' && (
              <DashboardView
                allTransactions={allTransactions}
                currentUser={{ ...profile, id: user?.id, role }}
                allUsers={allUsers}
                onOpenProfit={() => setShowProfitModal(true)}
                onTransactionDeleted={loadTransactions}
                isDark={isDark}
              />
            )}
            {activeTab === 'admin' && <AdminPanelView isDark={isDark} />}
            {activeTab === 'manager' && <ManagerPanelView />}
            {activeTab === 'chat' && <ChatView key="chat-view" onMessagesRead={loadUnreadMessages} />}
            {activeTab === 'messenger' && <MessengerView isDark={isDark} />}
            {activeTab === 'tours' && <ToursView isDark={isDark} />}
            {activeTab === 'contacts' && <ContactsView />}
            {activeTab === 'tasks' && <TasksView key="tasks-view" onTaskRead={loadUnreadTasks} />}
            {activeTab === 'tickets' && <TicketsView key="tickets-view" onTicketRead={loadUnreadTickets} />}
          </Suspense>
        </div>
        
        {/* Tour Search Button - на всех вкладках */}
        <TourSearchButton />
      </main>

      {/* Mobile Bottom Nav */}
      <div
        className={`md:hidden fixed bottom-0 left-0 w-full h-[70px] border-t z-40 flex items-center justify-around px-2 ${
          isDark ? 'bg-slate-900/95 border-slate-800 backdrop-blur-md' : 'bg-white/95 border-slate-200 backdrop-blur-md'
        }`}
      >
        {filteredTabs.slice(0, 4).map((tab) => {
          let badge = 0;
          if (tab.id === 'tasks') badge = unreadTasks;
          if (tab.id === 'tickets') badge = unreadTickets;
          if (tab.id === 'chat') badge = unreadMessages;
          
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as Tab)}
              className={`flex flex-col items-center justify-center w-full py-2 transition-colors relative touch-manipulation min-h-[56px] ${
                activeTab === tab.id 
                  ? badge > 0 
                    ? 'text-orange-600' 
                    : 'text-blue-600' 
                  : badge > 0
                  ? 'text-orange-500'
                  : 'text-slate-400 active:text-slate-600'
              }`}
            >
              <tab.icon size={22} strokeWidth={activeTab === tab.id ? 2.5 : 2} className="mb-0.5" />
              <span className="text-[10px] font-medium mt-0.5 leading-tight">{tab.label.split(' ')[0]}</span>
              {badge > 0 && (
                <span className="absolute top-0 right-[25%] bg-orange-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
        <button
          onClick={() => setShowMobileMenu(true)}
          className="flex flex-col items-center justify-center w-full py-2 text-slate-400 active:text-slate-600 transition-colors touch-manipulation min-h-[56px]"
        >
          <Grid size={22} className="mb-0.5" />
          <span className="text-[10px] font-medium mt-0.5">Меню</span>
        </button>
      </div>

      {/* Mobile Menu Modal */}
      {showMobileMenu && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setShowMobileMenu(false)}>
          <div 
            className={`absolute bottom-0 left-0 w-full max-h-[80vh] overflow-y-auto rounded-t-3xl p-6 space-y-2 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto mb-6"></div>
            
            {/* User Profile Section */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
              <img
                src={profile?.avatar || '/placeholder.svg?height=150&width=150'}
                className={`w-12 h-12 rounded-full border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                alt="Avatar"
              />
              <div className="flex-1 overflow-hidden">
                <div className={`text-base font-bold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {profile?.name}
                </div>
                <div className="text-sm text-slate-500 truncate capitalize">
                  {role === 'manager'
                    ? 'Руководитель'
                    : role === 'admin'
                    ? 'Админ'
                    : role === 'moderator'
                    ? 'Модератор'
                    : role === 'user'
                    ? 'Пользователь'
                    : 'Сотрудник'}
                </div>
              </div>
            </div>

            {/* All Tabs */}
            {filteredTabs.map((tab) => {
              let badge = 0;
              if (tab.id === 'tasks') badge = unreadTasks;
              if (tab.id === 'tickets') badge = unreadTickets;
              if (tab.id === 'chat') badge = unreadMessages;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as Tab);
                    setShowMobileMenu(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all touch-manipulation relative ${
                    activeTab === tab.id
                      ? badge > 0
                        ? 'bg-orange-500 text-white shadow-lg'
                        : 'bg-blue-600 text-white shadow-lg'
                      : badge > 0
                      ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                      : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 active:bg-slate-200 dark:active:bg-slate-700'
                  }`}
                >
                  <tab.icon size={20} />
                  <span className="font-medium text-sm flex-1 text-left">{tab.label}</span>
                  {badge > 0 && (
                    <span className="bg-white text-orange-600 dark:bg-slate-800 dark:text-orange-400 text-xs font-bold px-2 py-1 rounded-full">
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Theme Toggle */}
            <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
              <ThemeToggle />
            </div>

            {/* Settings & Logout */}
            <button 
              onClick={() => {
                setShowMobileMenu(false);
                setShowSettings(true);
              }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors touch-manipulation"
            >
              <Settings size={20} />
              <span className="font-medium text-sm">Настройки</span>
            </button>
            <button
              onClick={() => {
                setShowMobileMenu(false);
                signOut();
              }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors touch-manipulation"
            >
              <LogOut size={20} />
              <span className="font-medium text-sm">Выйти</span>
            </button>
          </div>
        </div>
      )}

      {showProfitModal && (
        <ProfitModal
          onAdd={handleAddTransaction}
          onDelete={handleDeleteTransaction}
          onClose={() => setShowProfitModal(false)}
          transactions={allTransactions}
          isDark={isDark}
        />
      )}

      {showSettings && (
        <SettingsModal
          onClose={() => setShowSettings(false)}
          isDark={isDark}
        />
      )}
    </div>
  );
}